import { useEffect, useRef, type FC } from 'react';
import DraggableModal from 'components/organisms/draggableModal';
import BaseButton from 'components/atoms/baseButton';

import StepRenderer from '../steps/step-renderer';
import { useClinicalRunbook } from '../use-clinical-runbook';
import { scrollToErrorElement } from '../../helpers';
import { useWeightLogic } from './use-weight-logic';
import { weightCriticalReviewConfig } from './critical-review';
import { weightMonthlyTouchpointConfig } from './monthly-touchpoint';
import s from '../styles.module.scss';

interface Props {
  idx: string | number;
  patientGuid: string;
  onClose: () => void;
}

/**
 * Weight Management review flow (regular weight critical reading — NOT CHF/Fluid).
 * Critical Reading Review → optional Monthly Touchpoint. Separate from the #1–#6 wizard.
 */
const WeightReview: FC<Props> = ({ idx, patientGuid, onClose }) => {
  const runbook = useClinicalRunbook({ patientGuid });
  const {
    formik,
    isAttemptNow,
    showTouchpoint,
    setShowTouchpoint,
    handleSave,
    errors,
    saving,
  } = useWeightLogic({ patientGuid, idx });
  const formWrapperRef = useRef<HTMLDivElement>(null);

  // Pre-fill the weight runbook answer from the real clinic runbook once loaded.
  const appliedRunbookRef = useRef(false);
  useEffect(() => {
    if (runbook.loading || appliedRunbookRef.current) return;
    appliedRunbookRef.current = true;
    if (formik.values.weightRunbook === undefined) {
      formik.setFieldValue(
        'weightRunbook',
        runbook.hasCriticalEntry ? 'has-entry' : 'no-entry',
      );
    }
  }, [runbook.loading, runbook.hasCriticalEntry, formik]);

  const onSave = async () => {
    const ok = await handleSave();
    if (!ok) {
      scrollToErrorElement(formWrapperRef.current);
      return;
    }
    onClose();
  };

  const footer = (
    <div className={s.footer}>
      <div className={s['footer-left']}>
        {isAttemptNow && !showTouchpoint && (
          <BaseButton
            type="secondary"
            label="Attempt now"
            onClick={() => setShowTouchpoint(true)}
            width={140}
          />
        )}
      </div>
      <div className={s['footer-right']}>
        <BaseButton
          label="Save"
          onClick={onSave}
          loading={saving}
          width={100}
        />
      </div>
    </div>
  );

  return (
    <DraggableModal
      title="Weight Management — Critical Reading Review"
      isOpen
      toggle={onClose}
      footer={footer}
    >
      <div className={s.wrapper} ref={formWrapperRef}>
        <StepRenderer
          items={weightCriticalReviewConfig}
          formik={formik}
          errors={errors}
        />
        {isAttemptNow && showTouchpoint && (
          <StepRenderer
            items={weightMonthlyTouchpointConfig}
            formik={formik}
            errors={errors}
          />
        )}
      </div>
    </DraggableModal>
  );
};

export default WeightReview;
